import { Link } from "react-router-dom";
import { Sparkles, Crown, ChevronRight, Heart, User, Globe, Bell, Users, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";

export default function HomeDemoTeaser() {
  const { t } = useLanguage();

  return (
    <section className="py-12 container mx-auto px-6">
      <Card className="relative overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-transparent to-amber-500/5">
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2" />
        <CardContent className="relative p-6 md:p-8">
          <div className="flex flex-col md:flex-row items-center gap-6">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-5 h-5 text-primary" />
                <span className="text-sm font-medium text-primary uppercase tracking-wider">{t.homeTeaser.personalization}</span>
              </div>
              <h2 className="font-display text-2xl md:text-3xl font-bold text-foreground mb-3">
                {t.homeTeaser.title}
              </h2>
              <p className="text-muted-foreground mb-4 max-w-lg">
                {t.homeTeaser.subtitle}
              </p>

              <div className="flex flex-wrap gap-3 mb-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Globe className="w-4 h-4 text-primary" />
                  <span>{t.homeTeaser.nationTracking}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Heart className="w-4 h-4 text-primary" />
                  <span>{t.homeTeaser.teamAlerts}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <User className="w-4 h-4 text-primary" />
                  <span>{t.homeTeaser.playerStats}</span>
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <Link to="/dashboard">
                  <Button className="gap-2">
                    <Crown className="w-4 h-4" />
                    {t.homeTeaser.seeDemo}
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/auth">
                  <Button variant="outline" className="gap-2">
                    <Bell className="w-4 h-4" />
                    {t.homeTeaser.signUpFree}
                  </Button>
                </Link>
              </div>
              
              <div className="flex items-center gap-2 mt-3 text-xs text-muted-foreground">
                <Users className="w-3.5 h-3.5 text-primary" />
                <span>{t.homeTeaser.joinMessage}</span>
              </div>
            </div>

            {/* Mini preview */}
            <div className="md:w-72 w-full">
              <div className="space-y-2 opacity-80">
                <div className="bg-secondary/50 rounded-lg p-3 border border-border">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">🇸🇪</span>
                    <span className="font-medium text-sm text-foreground">Sweden</span>
                    <span className="text-xs text-muted-foreground ml-auto">Your Nation</span>
                  </div>
                  <div className="text-xs text-muted-foreground">Next: vs Canada – Feb 14</div>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 border border-border">
                  <div className="flex items-center gap-2">
                    <img src="https://assets.nhle.com/logos/nhl/svg/EDM_light.svg" alt="" className="w-5 h-5 object-contain" />
                    <span className="text-sm font-medium text-foreground">Oilers</span>
                    <span className="w-2 h-2 bg-score-live rounded-full animate-pulse ml-auto" />
                    <span className="text-xs text-score-live font-semibold">LIVE 3-2</span>
                  </div>
                </div>
                <div className="bg-secondary/50 rounded-lg p-3 border border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-6 h-6 rounded-full bg-secondary flex items-center justify-center text-[10px] font-bold">CM</div>
                    <span className="text-sm font-medium text-foreground">McDavid</span>
                    <span className="text-xs text-primary font-bold ml-auto">80 PTS</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Draft Watch Teaser */}
      <Card className="mt-4 border-border/50 bg-gradient-to-r from-amber-500/5 via-transparent to-primary/5 hover:border-primary/30 transition-colors group cursor-pointer"
        onClick={() => document.getElementById('players')?.scrollIntoView({ behavior: 'smooth' })}
      >
        <CardContent className="p-4 md:p-5">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center shrink-0">
              <Star className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-display text-base md:text-lg font-bold text-foreground">
                {t.homeTeaser.draftWatch}
              </h3>
              <p className="text-xs md:text-sm text-muted-foreground">
                {t.homeTeaser.draftWatchDesc}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors shrink-0" />
          </div>
        </CardContent>
      </Card>
    </section>
  );
}
